import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  AlertTriangle,
  Banknote,
  CheckCircle2,
  Download,
  HandCoins,
  Loader2,
  PauseCircle,
  Undo2,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { payrollOpsService } from "../../../services/api";
import { formatInr } from "./runPayrollUtils";
import { exportPaymentLedgerExcel } from "./exportPaymentLedgerExcel";
import EmpCodeNameSearchBar from "../../Common/EmpCodeNameSearchBar";
import { filterRowsByEmpCodeOrName } from "../../../lib/empCodeNameSearch";

function inr(n) {
  return formatInr(n);
}

function statusMeta(status) {
  const s = String(status || "pending");
  if (s === "paid" || s === "recovery_cleared") {
    return { label: s === "paid" ? "Paid" : "Recovery cleared", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" };
  }
  if (s === "hold") return { label: "Hold", cls: "bg-amber-100 text-amber-900 border-amber-200" };
  if (s === "partial_hold" || s === "partial_paid") {
    return { label: "Partial", cls: "bg-orange-100 text-orange-900 border-orange-200" };
  }
  if (s === "recovery_open") {
    return { label: "Recovery due", cls: "bg-rose-100 text-rose-900 border-rose-200" };
  }
  return { label: "Unpaid", cls: "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))] border-[hsl(var(--ds-border))]" };
}

function isSettledStatus(status) {
  return ["paid", "hold", "partial_hold", "recovery_cleared"].includes(String(status || "pending"));
}

function pickStreamStatusLabel(streamRows = []) {
  if (!streamRows.length) return "Unpaid";
  const statuses = streamRows.map((r) => String(r.status || "pending"));
  if (statuses.every((s) => s === "paid" || s === "recovery_cleared")) {
    return statuses.some((s) => s === "recovery_cleared") && statuses.every((s) => s === "recovery_cleared")
      ? "Recovery cleared"
      : "Paid";
  }
  if (statuses.some((s) => s === "recovery_open")) return "Recovery due";
  if (statuses.some((s) => s === "partial_hold" || s === "partial_paid")) return "Partial";
  if (statuses.some((s) => s === "hold")) return "Hold";
  return "Unpaid";
}

export function DisbursementActions({
  darkMode,
  hasProcessed,
  finalized,
  unpaidCount = 0,
  payEligibleCount = 0,
  settledCount = 0,
  totalCount = 0,
  allPaid = false,
  complete = false,
  busy = false,
  loadingAction = null,
  onOpenAction,
  onExportLedger = null,
  exportBusy = false,
}) {
  if (!hasProcessed) return null;

  // Payments stay open after Finalise (1A)
  const blocked = busy;
  const anyOpening = !!loadingAction;
  const isLoading = (key) => loadingAction === key;

  const ActionIcon = ({ actionKey, IdleIcon }) =>
    isLoading(actionKey) ? <Loader2 size={13} className="animate-spin" /> : <IdleIcon size={13} />;

  return (
    <>
      {finalized && (
        <span className="text-[11px] font-medium text-emerald-700">
          Month finalised — payments still open
        </span>
      )}
      {complete && (
        <span className="text-[11px] font-semibold text-emerald-600">
          All settled ({settledCount}/{totalCount || settledCount})
        </span>
      )}
      {typeof onExportLedger === "function" && (
        <button
          type="button"
          disabled={exportBusy || busy}
          onClick={onExportLedger}
          className={`inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs font-semibold disabled:opacity-45 ${
            darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
          }`}
          title="Export cumulative payment ledger Excel"
        >
          {exportBusy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
          Export ledger
        </button>
      )}
      {allPaid ? (
        <button
          type="button"
          disabled
          className="inline-flex items-center gap-1 rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white opacity-90"
          title="All employees are marked paid"
        >
          <CheckCircle2 size={13} /> Paid
        </button>
      ) : (
        <button
          type="button"
          disabled={blocked || anyOpening || payEligibleCount === 0}
          onClick={() => onOpenAction?.("disburse_paid")}
          title="Pay pending/hold employees — All / Selected / Group-wise"
          className="inline-flex items-center gap-1 rounded bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-45"
        >
          <ActionIcon actionKey="disburse_paid" IdleIcon={CheckCircle2} />
          Pay{payEligibleCount > 0 ? ` (${payEligibleCount})` : ""}
        </button>
      )}
      <button
        type="button"
        disabled={blocked || anyOpening || unpaidCount === 0}
        onClick={() => onOpenAction?.("disburse_hold")}
        title="Hold unpaid employees — All / Selected / Group-wise"
        className="inline-flex items-center gap-1 rounded bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-45"
      >
        <ActionIcon actionKey="disburse_hold" IdleIcon={PauseCircle} />
        Hold{unpaidCount > 0 ? ` (${unpaidCount})` : ""}
      </button>
      <button
        type="button"
        disabled={blocked || anyOpening || unpaidCount === 0}
        onClick={() => onOpenAction?.("disburse_partial")}
        title="Pay partial — All / Selected / Group-wise with amount or percent"
        className={`inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs font-semibold disabled:opacity-45 ${
          darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
        }`}
      >
        <ActionIcon actionKey="disburse_partial" IdleIcon={HandCoins} />
        Pay partial
      </button>
      <button
        type="button"
        disabled={blocked || anyOpening || settledCount === 0}
        onClick={() => onOpenAction?.("disburse_unpay")}
        title="Unpay — revert paid / hold / partial back to unpaid"
        className={`inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs font-semibold disabled:opacity-45 ${
          darkMode ? "border-[hsl(var(--ds-border))] text-amber-200" : "border-amber-300 text-amber-900 bg-amber-50"
        }`}
      >
        <ActionIcon actionKey="disburse_unpay" IdleIcon={Undo2} />
        Unpay{settledCount > 0 ? ` (${settledCount})` : ""}
      </button>
    </>
  );
}

export default function PayrollDisbursementPanel({
  darkMode,
  payMonth,
  hasProcessed = false,
  allProcessed,
  finalized,
  streamMode = "both",
  onChanged,
  onRowsChange = null,
  reloadToken = 0,
  paymentDate = "",
}) {
  const unlocked = hasProcessed || allProcessed;
  const [rows, setRows] = useState([]);
  const [carryForward, setCarryForward] = useState([]);
  const [openHolds, setOpenHolds] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [search, setSearch] = useState("");
  const [releaseId, setReleaseId] = useState(null);
  const [rowBusyKey, setRowBusyKey] = useState(null);

  const border = darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]";
  const muted = darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]";
  const card = darkMode ? "bg-[hsl(var(--ds-muted))]" : "bg-[hsl(var(--ds-surface))]";

  const load = useCallback(async () => {
    if (!payMonth) return;
    setLoading(true);
    try {
      const res = await payrollOpsService.listDisbursements(payMonth);
      const nextRows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      const nextCarry = Array.isArray(res?.data?.carryForward) ? res.data.carryForward : [];
      setRows(nextRows);
      setCarryForward(nextCarry);
      setOpenHolds(Array.isArray(res?.data?.openHolds) ? res.data.openHolds : []);
      setTotals(res?.data?.totals || null);
      onRowsChange?.([...nextRows, ...nextCarry]);
    } catch (err) {
      setRows([]);
      setCarryForward([]);
      setOpenHolds([]);
      setTotals(null);
      onRowsChange?.([]);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load disbursement");
    } finally {
      setLoading(false);
    }
  }, [payMonth, onRowsChange]);

  useEffect(() => {
    load();
  }, [load, reloadToken]);

  const sorted = useMemo(() => {
    const grouped = new Map();
    (rows || []).forEach((r) => {
      const eid = Number(r.employeeId) || 0;
      if (!eid) return;
      if (!grouped.has(eid)) {
        grouped.set(eid, {
          employeeId: eid,
          empCode: r.empCode || String(eid),
          empName: r.empName || "",
          hasFnf: false,
          salaryNet: 0,
          salaryPaid: 0,
          salaryHold: 0,
          salaryStatus: "Unpaid",
          fnfNet: 0,
          fnfPaid: 0,
          fnfHold: 0,
          fnfStatus: "Unpaid",
        });
      }
      const row = grouped.get(eid);
      const stream = String(r.stream || "salary");
      if (stream === "fnf") {
        row.hasFnf = true;
        row.fnfNet = Number(r.netPay) || 0;
        row.fnfPaid = Number(r.paidAmount) || 0;
        row.fnfHold = Number(r.holdAmount) || 0;
      } else {
        row.salaryNet = Number(r.netPay) || 0;
        row.salaryPaid = Number(r.paidAmount) || 0;
        row.salaryHold = Number(r.holdAmount) || 0;
      }
      if (!row.empCode && r.empCode) row.empCode = r.empCode;
      if (!row.empName && r.empName) row.empName = r.empName;
    });

    const list = [...grouped.values()].map((r) => {
      const sourceRows = (rows || []).filter((x) => Number(x.employeeId) === Number(r.employeeId));
      const salaryRows = sourceRows.filter((x) => String(x.stream || "salary") !== "fnf");
      const fnfRows = sourceRows.filter((x) => String(x.stream || "salary") === "fnf");
      return {
        ...r,
        salaryStatus: pickStreamStatusLabel(salaryRows),
        fnfStatus: pickStreamStatusLabel(fnfRows),
      };
    });

    const scopedList =
      streamMode === "fnf" ? list.filter((r) => r.hasFnf) : list;

    if (!sortKey) return scopedList;
    scopedList.sort((a, b) => {
      const va = a?.[sortKey];
      const vb = b?.[sortKey];
      const na = Number(va);
      const nb = Number(vb);
      let cmp;
      if (Number.isFinite(na) && Number.isFinite(nb) && String(va).trim() !== "" && String(vb).trim() !== "") {
        cmp = na - nb;
      } else {
        cmp = String(va ?? "").localeCompare(String(vb ?? ""), undefined, {
          numeric: true,
          sensitivity: "base",
        });
      }
      return sortDir === "asc" ? cmp : -cmp;
    });
    return scopedList;
  }, [rows, sortKey, sortDir, streamMode]);

  const filteredSorted = useMemo(
    () => filterRowsByEmpCodeOrName(sorted, search),
    [sorted, search]
  );

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const settledCount = rows.filter((r) => isSettledStatus(r.status)).length;
  const complete = rows.length > 0 && settledCount === rows.length;

  const runRelease = async () => {
    if (!releaseId) return;
    setBusy(true);
    try {
      await payrollOpsService.releasePaymentHold(releaseId, payMonth);
      toast.success("Hold released");
      setReleaseId(null);
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Release failed");
    } finally {
      setBusy(false);
    }
  };

  const runDisbursement = useCallback(
    async ({ employeeId, stream, action, notes = null, disbursementIds = null }) => {
      const payload = {
        payMonth,
        action,
        stream,
        notes,
        paymentDate: paymentDate || new Date().toISOString().slice(0, 10),
      };
      if (Array.isArray(disbursementIds) && disbursementIds.length) {
        payload.disbursementIds = disbursementIds;
      } else {
        payload.employeeIds = [Number(employeeId)];
      }
      return payrollOpsService.applyDisbursement(payload);
    },
    [payMonth, paymentDate]
  );

  const exportLedger = async () => {
    setExportBusy(true);
    try {
      const res = await payrollOpsService.listPaymentLedger({ contextMonth: payMonth });
      const ledgerRows = Array.isArray(res?.data?.rows) ? res.data.rows : [];
      if (!ledgerRows.length) {
        toast.error("No payment batches yet — pay at least one employee first");
        return;
      }
      await exportPaymentLedgerExcel({ rows: ledgerRows, payMonth });
      toast.success("Payment ledger exported");
    } catch (err) {
      toast.error(err?.message || "Export failed");
    } finally {
      setExportBusy(false);
    }
  };

  const runEmployeePreset = async (row, preset) => {
    if (!row?.employeeId) return;
    const key = `${row.employeeId}:${preset}`;
    setRowBusyKey(key);
    try {
      if (preset === "salary_now_fnf_hold") {
        if (String(row.salaryStatus || "").toLowerCase() !== "paid") {
          await runDisbursement({
            employeeId: row.employeeId,
            stream: "salary",
            action: "paid",
            notes: "Quick preset: salary now",
          });
        }
        if (row.hasFnf && String(row.fnfStatus || "").toLowerCase() === "unpaid") {
          await runDisbursement({
            employeeId: row.employeeId,
            stream: "fnf",
            action: "hold",
            notes: "Quick preset: hold fnf",
          });
        }
        toast.success("Salary paid, F&F kept for later");
      } else if (preset === "pay_salary") {
        await runDisbursement({
          employeeId: row.employeeId,
          stream: "salary",
          action: "paid",
          notes: "Quick preset: pay salary",
        });
        toast.success("Salary paid");
      } else if (preset === "hold_fnf") {
        await runDisbursement({
          employeeId: row.employeeId,
          stream: "fnf",
          action: "hold",
          notes: "Quick preset: hold fnf",
        });
        toast.success("F&F moved to hold");
      } else if (preset === "release_fnf") {
        await runDisbursement({
          employeeId: row.employeeId,
          stream: "fnf",
          action: "paid",
          notes: "Quick preset: release fnf",
        });
        toast.success("F&F released");
      }
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Quick action failed");
    } finally {
      setRowBusyKey(null);
    }
  };

  if (!unlocked) {
    return (
      <div
        className={`flex items-start gap-3 rounded-xl border px-4 py-4 ${border} ${
          darkMode ? "bg-amber-950/30 text-amber-100" : "bg-amber-50 text-amber-950"
        }`}
      >
        <AlertTriangle className="mt-0.5 shrink-0" size={18} />
        <div>
          <p className="text-sm font-semibold">Process payroll first</p>
          <p className={`mt-1 text-xs ${darkMode ? "text-amber-200/80" : "text-amber-800"}`}>
            Disbursement opens after at least one employee is processed. Pay Salary and F&F in
            batches with a payment date — unpaid amounts carry forward until paid. Finalise does not
            require full disbursement.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex h-40 items-center justify-center gap-2 rounded-xl border ${border}`}>
        <Loader2 className="animate-spin text-[hsl(var(--ds-primary))]" size={18} />
        <span className={`text-sm ${muted}`}>Loading disbursement register…</span>
      </div>
    );
  }

  const kpi = [
    {
      label: "Salary + F&F lines",
      value: String(rows.length),
      icon: Banknote,
      color: "text-sky-600",
    },
    {
      label: "Paid (count)",
      value: String(totals?.paidCount ?? rows.filter((r) => r.status === "paid").length),
      icon: CheckCircle2,
      color: "text-emerald-600",
    },
    {
      label: "Unpaid (₹)",
      value: String(
        totals?.unpaid != null
          ? inr(totals.unpaid)
          : totals?.pendingCount ?? rows.filter((r) => !isSettledStatus(r.status)).length
      ),
      icon: HandCoins,
      color: "text-[hsl(var(--ds-muted-fg))]",
    },
    {
      label: "Recovery due (₹)",
      value: String(totals?.recoveryDue != null ? inr(totals.recoveryDue) : "0"),
      icon: PauseCircle,
      color: "text-rose-600",
    },
  ];

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-3 overflow-hidden">
      {finalized && (
        <div
          className={`shrink-0 rounded-lg border px-3 py-2 text-xs ${border} ${
            darkMode ? "bg-emerald-950/30 text-emerald-200" : "bg-emerald-50 text-emerald-900"
          }`}
        >
          Month is finalised — payroll calculation is locked. You can still pay / hold / export the
          payment register (unpaid carries forward).
        </div>
      )}

      {(totals?.monthPendingCount > 0 || carryForward.length > 0) && (
        <p className="shrink-0 text-xs text-amber-800 dark:text-amber-200">
          {totals?.monthPendingCount || 0} open this month
          {carryForward.length > 0 ? ` · ${carryForward.length} carry-forward from other months` : ""}
          . Finalise is allowed with unpaid balances.
        </p>
      )}

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <EmpCodeNameSearchBar
          value={search}
          onChange={setSearch}
          darkMode={darkMode}
          showCount={sorted.length > 0}
          total={sorted.length}
          filtered={filteredSorted.length}
        />
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        <button
          type="button"
          disabled={exportBusy}
          onClick={exportLedger}
          className={`inline-flex items-center gap-1 rounded border px-3 py-1.5 text-xs font-semibold disabled:opacity-45 ${border}`}
        >
          {exportBusy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
          Export payment ledger
        </button>
        </div>
      </div>

      <div className="grid shrink-0 gap-1.5 sm:grid-cols-2 xl:grid-cols-4">
        {kpi.map((k) => (
          <div key={k.label} className={`rounded-md border px-2.5 py-1.5 ${border} ${card}`}>
            <div className="flex items-center justify-between gap-2">
              <p className={`text-[10px] font-medium uppercase tracking-wide ${muted}`}>{k.label}</p>
              <k.icon size={12} className={k.color} />
            </div>
            <p className="text-sm font-semibold tabular-nums leading-tight">{k.value}</p>
          </div>
        ))}
      </div>

      <div className={`ds-table-scroll-fill rounded-xl border text-xs ${border}`}>
        {!filteredSorted.length && sorted.length > 0 ? (
          <p className={`px-4 py-8 text-center ${muted}`}>
            No employees match “{search.trim()}”.
          </p>
        ) : (
        <table
          className={`w-full ${
            streamMode === "fnf" ? "min-w-[980px]" : streamMode === "salary" ? "min-w-[980px]" : "min-w-[1360px]"
          }`}
        >
          <thead className={`sticky top-0 z-[1] ${darkMode ? "bg-[hsl(var(--ds-bg))]" : "bg-[hsl(var(--ds-muted))]"}`}>
            <tr>
              <th className="px-3 py-2 text-left font-bold">Sl. No</th>
              {[
                ["empCode", "Code", "left"],
                ["empName", "Employee", "left"],
                ...(streamMode !== "fnf"
                  ? [
                      ["salaryNet", "Salary Net", "right"],
                      ["salaryPaid", "Salary Paid", "right"],
                      ["salaryHold", "Salary Hold", "right"],
                      ["salaryStatus", "Salary Status", "left"],
                    ]
                  : []),
                ...(streamMode !== "salary"
                  ? [
                      ["fnfNet", "F&F Net", "right"],
                      ["fnfPaid", "F&F Paid", "right"],
                      ["fnfHold", "F&F Hold", "right"],
                      ["fnfStatus", "F&F Status", "left"],
                    ]
                  : []),
                ["quickAction", streamMode === "fnf" ? "F&F Actions" : "Quick Actions", "left"],
              ].map(([key, label, align]) => (
                <th
                  key={key}
                  className={`px-3 py-2 font-bold ${align === "right" ? "text-right" : "text-left"}`}
                >
                  <button
                    type="button"
                    onClick={() => toggleSort(key)}
                    className={`font-bold hover:text-[hsl(var(--ds-primary))] ${
                      align === "right" ? "w-full text-right" : "text-left"
                    } ${sortKey === key ? "text-[hsl(var(--ds-primary))]" : ""}`}
                  >
                    {label}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredSorted.map((r, idx) => {
              const salarySt = statusMeta((r.salaryStatus || "Unpaid").toLowerCase());
              const fnfSt = statusMeta((r.fnfStatus || "Unpaid").toLowerCase());
              return (
                <tr
                  key={`${r.employeeId}-${idx}`}
                  className={`border-t ${darkMode ? "border-[#3C3C3C]" : "border-[hsl(var(--ds-border))]"}`}
                >
                  <td className="px-3 py-2 tabular-nums text-[hsl(var(--ds-muted-fg))]">{idx + 1}</td>
                  <td className="px-3 py-2 font-medium">{r.empCode || "—"}</td>
                  <td className="px-3 py-2">{r.empName || "—"}</td>
                  {streamMode !== "fnf" ? (
                    <>
                      <td className="px-3 py-2 text-right tabular-nums">{inr(r.salaryNet)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                        {inr(r.salaryPaid)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-amber-700">
                        {inr(r.salaryHold)}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${salarySt.cls}`}
                        >
                          {salarySt.label}
                        </span>
                      </td>
                    </>
                  ) : null}
                  {streamMode !== "salary" ? (
                    <>
                      <td className="px-3 py-2 text-right tabular-nums">{inr(r.fnfNet)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-emerald-700">
                        {inr(r.fnfPaid)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-amber-700">
                        {inr(r.fnfHold)}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-semibold ${fnfSt.cls}`}
                        >
                          {fnfSt.label}
                        </span>
                      </td>
                    </>
                  ) : null}
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {streamMode !== "fnf" ? (
                        <button
                          type="button"
                          disabled={
                            finalized ||
                            !!rowBusyKey ||
                            String(r.salaryStatus || "").toLowerCase() === "paid"
                          }
                          onClick={() => runEmployeePreset(r, "pay_salary")}
                          className="rounded border border-sky-300 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-800 disabled:opacity-45"
                        >
                          Pay Salary
                        </button>
                      ) : null}
                      {r.hasFnf ? (
                        <>
                          <button
                            type="button"
                            disabled={
                              finalized ||
                              !!rowBusyKey ||
                              String(r.fnfStatus || "").toLowerCase() !== "unpaid"
                            }
                            onClick={() => runEmployeePreset(r, "hold_fnf")}
                            className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-900 disabled:opacity-45"
                          >
                            Hold F&F
                          </button>
                          <button
                            type="button"
                            disabled={
                              finalized ||
                              !!rowBusyKey ||
                              String(r.fnfStatus || "").toLowerCase() === "paid"
                            }
                            onClick={() => runEmployeePreset(r, "release_fnf")}
                            className="rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800 disabled:opacity-45"
                          >
                            Release F&F
                          </button>
                          <button
                            type="button"
                            disabled={
                              finalized ||
                              !!rowBusyKey ||
                              streamMode === "fnf" ||
                              (String(r.salaryStatus || "").toLowerCase() === "paid" &&
                                String(r.fnfStatus || "").toLowerCase() !== "unpaid")
                            }
                            onClick={() => runEmployeePreset(r, "salary_now_fnf_hold")}
                            className="rounded border border-violet-300 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-800 disabled:opacity-45"
                          >
                            Salary now + Hold F&F
                          </button>
                        </>
                      ) : null}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
        {!sorted.length && (
          <p className={`px-4 py-8 text-center text-sm ${muted}`}>
            No processed employees for this month.
          </p>
        )}
      </div>

      {!!carryForward.length && (
        <div className={`rounded-xl border p-3 ${border} ${card}`}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <h4 className="text-sm font-semibold">Carry-forward unpaid / recovery</h4>
            <span className={`text-[11px] ${muted}`}>{carryForward.length} open</span>
          </div>
          <div className="max-h-48 overflow-auto text-xs">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left">Month</th>
                  <th className="px-2 py-1 text-left">Employee</th>
                  <th className="px-2 py-1 text-left">Stream</th>
                  <th className="px-2 py-1 text-right">Unpaid</th>
                  <th className="px-2 py-1 text-right">Recovery</th>
                  <th className="px-2 py-1 text-right">Hold</th>
                  <th className="px-2 py-1 text-left">Status</th>
                  <th className="px-2 py-1 text-right">Pay</th>
                </tr>
              </thead>
              <tbody>
                {carryForward.map((r) => {
                  const st = statusMeta(r.status);
                  return (
                    <tr key={r.id} className={darkMode ? "border-t border-[#3C3C3C]" : "border-t"}>
                      <td className="px-2 py-1">{r.payMonth}</td>
                      <td className="px-2 py-1">
                        {r.empCode} — {r.empName}
                      </td>
                      <td className="px-2 py-1 uppercase">{r.stream || "salary"}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{inr(r.unpaid)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{inr(r.recoveryDue)}</td>
                      <td className="px-2 py-1 text-right tabular-nums">{inr(r.holdAmount)}</td>
                      <td className="px-2 py-1">
                        <span className={`rounded border px-1.5 py-0.5 text-[10px] font-semibold ${st.cls}`}>
                          {st.label}
                        </span>
                      </td>
                      <td className="px-2 py-1 text-right">
                        {(Number(r.unpaid) || 0) > 0 && (
                          <button
                            type="button"
                            disabled={busy}
                            className="text-[hsl(var(--ds-primary))] disabled:opacity-40"
                            onClick={async () => {
                              try {
                                await runDisbursement({
                                  disbursementIds: [r.id],
                                  stream: r.stream || "salary",
                                  action: "paid",
                                  notes: "Carry-forward payment",
                                });
                                toast.success("Carry-forward paid");
                                await load();
                                onChanged?.();
                              } catch (err) {
                                toast.error(err?.response?.data?.message || err?.message || "Pay failed");
                              }
                            }}
                          >
                            Pay
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!!openHolds.length && (
        <div className={`rounded-xl border p-3 ${border} ${card}`}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <h4 className="text-sm font-semibold">Open payment holds</h4>
            <span className={`text-[11px] ${muted}`}>{openHolds.length} open</span>
          </div>
          <div className="max-h-40 overflow-auto text-xs">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="px-2 py-1 text-left">Employee</th>
                  <th className="px-2 py-1 text-right">Remaining</th>
                  <th className="px-2 py-1 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {openHolds.map((h) => (
                  <tr key={h.id} className={darkMode ? "border-t border-[#3C3C3C]" : "border-t"}>
                    <td className="px-2 py-1">
                      {h.empCode || h.employeeId} — {h.empName || ""}
                    </td>
                    <td className="px-2 py-1 text-right tabular-nums">{inr(h.remainingAmount)}</td>
                    <td className="px-2 py-1 text-right">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => setReleaseId(h.id)}
                        className="text-[hsl(var(--ds-primary))] disabled:opacity-40"
                      >
                        Release
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {releaseId &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[100060] flex items-center justify-center bg-black/50 p-4"
            onClick={() => setReleaseId(null)}
            role="presentation"
          >
            <div
              className={`w-full max-w-sm rounded-xl border p-4 shadow-xl ${
                darkMode ? "border-[#3C3C3C] bg-[hsl(var(--ds-surface))]" : "bg-[hsl(var(--ds-surface))] border-[hsl(var(--ds-border))]"
              }`}
              onClick={(e) => e.stopPropagation()}
              role="dialog"
            >
              <div className="mb-2 flex items-center justify-between">
                <h3 className="text-sm font-semibold">Release hold?</h3>
                <button type="button" onClick={() => setReleaseId(null)} className="rounded p-1">
                  <X size={16} />
                </button>
              </div>
              <p className={`text-xs ${muted}`}>This releases the open payment hold.</p>
              <div className="mt-4 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setReleaseId(null)}
                  className="rounded border px-3 py-1.5 text-xs"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={busy}
                  onClick={runRelease}
                  className="rounded bg-[hsl(var(--ds-primary))] px-3 py-1.5 text-xs text-white disabled:opacity-50"
                >
                  Release
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
